import type { IInnovationServiceProvider } from '#domain/innovation/innovation.service.interface';
import ApiClient from '#infra/config/api.client';
import type {
  InnovationType,
  InnovationsToDeleteType,
  KpiWeeklyWithDetentionSalesRateType,
  NonDetentionStoreType
} from '#root/domain/innovation/innovation.schema';
import type { Format, InternalOptions } from '#root/global.type';

export class InnovationServiceProvider implements IInnovationServiceProvider {
  async getInnovationMetaData(
    barcode: string,
    format: Format,
    internalOptions?: InternalOptions
  ): Promise<InnovationType> {
    let url = `/innovation/${barcode}/${format}`;

    if (internalOptions) {
      const searchParams = new URLSearchParams({
        startDate: internalOptions.startDate.toISOString(),
        endDate: internalOptions.endDate.toISOString()
      });

      url += `?${searchParams.toString()}`;
    }

    const { data } = await ApiClient.get(url);

    return data;
  }

  async getNonDetentionStores(
    barcode: string,
    format: Format,
    internalOptions?: InternalOptions
  ): Promise<NonDetentionStoreType[]> {
    let url = `/innovation/non-detention-stores/${barcode}/${format}`;

    if (internalOptions) {
      const searchParams = new URLSearchParams({
        startDate: internalOptions.startDate.toISOString(),
        endDate: internalOptions.endDate.toISOString()
      });

      url += `?${searchParams.toString()}`;
    }

    const { data } = await ApiClient.get(url);

    if (data) {
      return data;
    }

    return [];
  }

  async getPerformanceKpiWeekly(
    barcode: string,
    format: Format,
    internalOptions?: InternalOptions
  ): Promise<KpiWeeklyWithDetentionSalesRateType> {
    let url = `/innovation/weekly/${barcode}/${format}`;

    if (internalOptions) {
      const searchParams = new URLSearchParams({
        startDate: internalOptions.startDate.toISOString(),
        endDate: internalOptions.endDate.toISOString()
      });

      url += `?${searchParams.toString()}`;
    }

    const { data: kpisWeeklyResponse } = await ApiClient.get(url);

    const adaptedKpiWeekly: KpiWeeklyWithDetentionSalesRateType = {
      weeklyKpis: kpisWeeklyResponse.weeklyKpis?.map((item: { weekStartDate: string | number | Date }) => ({
        ...item,
        weekStartDate: new Date(item.weekStartDate)
      }))
    };

    return adaptedKpiWeekly;
  }

  async searchInnovations(search: string): Promise<string[]> {
    const response = await ApiClient.get(`/innovation?search=${search}`);
    const innovations = response.data;

    return innovations;
  }

  async searchInnovationsByBarcodes(search: string): Promise<string[]> {
    const response = await ApiClient.get(`/innovation/barcodes?search=${search}`);
    const innovations = response.data;

    return innovations;
  }

  async addInnovations(list: string[]): Promise<unknown> {
    return ApiClient.post('/innovation', { innovations: list });
  }

  async deleteInnovation(innovationsToDelete: InnovationsToDeleteType): Promise<unknown> {
    return ApiClient.delete('/innovation', { data: innovationsToDelete });
  }
}
